import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  chartLegendOptions,
  CustomGridStackWidget,
  GridWidgetConfigService,
  mapLegendOptions,
  mapLegendPositionOptions,
  mapTooltipStyleDropdownItems,
  PoiConf,
  TeaserImage,
  TeaserItem,
  WidgetConfig,
  WidgetType,
} from '../../../services/grid-widget-config.service';
import { ConfigFormOptions } from '../../../models/widget';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { DropdownItem } from '../../../models/form/basic';
import { NgClass, NgForOf, NgIf, NgOptimizedImage } from '@angular/common';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { CheckboxModule } from 'primeng/checkbox';
import { debounceTime } from 'rxjs';
import { ColorPickerModule } from 'primeng/colorpicker';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownOptionComponent } from '../../atoms/dropdown-option/dropdown-option.component';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { Button, ButtonDirective } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { StyleClassModule } from 'primeng/styleclass';
import { RadioButtonModule } from 'primeng/radiobutton';
import { NgxSliderComponent } from '../../../ngx-slider/ngx-slider.component';
import { Colorset, fallbackColors } from '../../../models/colorset';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DataGroupProperty } from '../../../stores/indicatpr-data/indicator-data.widget-selectors';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { DataLayer } from '../../../models/data-layer';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { FeatureCheckboxGroupComponent } from '../feature-checkbox-group/feature-checkbox-group.component';
import { FeatureDropdownComponent } from '../../atoms/feature-dropdown/feature-dropdown.component';
import { IndicatorDropdownComponent } from '../../atoms/indicator-dropdown/indicator-dropdown.component';
import { LayerDropdownComponent } from '../../atoms/layer-dropdown/layer-dropdown.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { YearCheckboxGroupComponent } from '../year-checkbox-group/year-checkbox-group.component';
import { DataLayerType } from '../../../models/data-layer-type';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { Indicator } from '../../../models/indicator';
import { AccordionModule } from 'primeng/accordion';
import { BadgeModule } from 'primeng/badge';
import { ComparisonValueSelectComponent } from '../../atoms/comparison-value-select/comparison-value-select.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { swap } from '../../../utility/array.utility';
import { HeaderComponent } from '../../atoms/header/header.component';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { WegweiserFastSwitchComponent } from '../wegweiser-fast-switch/wegweiser-fast-switch.component';
import { WegweiserWidgetConfigComponent } from '../wegweiser-widget-config/wegweiser-widget-config.component';
import { leafletTileLayerOptions } from '../../../models/commune';
import { WidgetHelperService } from '../../../services/widget/widget-helper.service';
import { EditorModule } from 'primeng/editor';
import { hrefValidator } from '../../../validators/href.validator';
import { TeaserItemDropdownComponent } from '../../atoms/teaser-item-dropdown/teaser-item-dropdown.component';
import { CardModule } from 'primeng/card';
import { ImageStore } from '../../../stores/image/image.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorData } from '../../../models/indicator-data';
import { ColorsComponent } from '../../atoms/colors/colors.component';
import { ChartConfigVisualComponent } from '../../atoms/chart-config-visual/chart-config-visual.component';
import {
  ChartConfigPresetsComponent,
  ChartPreset,
} from '../../atoms/chart-config-presets/chart-config-presets.component';
import { getState } from '@ngrx/signals';
import { SvgIconComponent } from '../../atoms/svg-icon/svg-icon.component';
import { DebugComponent } from '../../atoms/debug/debug.component';

@Component({
  selector: 'app-widget-config-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DropdownModule,
    NgIf,
    ColorsetDropdownComponent,
    FormFieldWrapperComponent,
    CheckboxModule,
    ColorPickerModule,
    InputTextModule,
    DropdownOptionComponent,
    InputTextareaModule,
    NgForOf,
    DividerModule,
    ButtonDirective,
    NgOptimizedImage,
    StyleClassModule,
    RadioButtonModule,
    NgxSliderComponent,
    SelectButtonModule,
    FormsModule,
    FeatureCheckboxGroupComponent,
    FeatureDropdownComponent,
    IndicatorDropdownComponent,
    LayerDropdownComponent,
    MultiSelectModule,
    YearCheckboxGroupComponent,
    AccordionModule,
    BadgeModule,
    ComparisonValueSelectComponent,
    InputNumberModule,
    HeaderComponent,
    ScrollPanelModule,
    WegweiserFastSwitchComponent,
    WegweiserWidgetConfigComponent,
    EditorModule,
    TeaserItemDropdownComponent,
    Button,
    CardModule,
    ColorsComponent,
    ChartConfigVisualComponent,
    ChartConfigPresetsComponent,
    SvgIconComponent,
    DebugComponent,
  ],
  templateUrl: './widget-config-form.component.html',
  styleUrl: './widget-config-form.component.scss',
})
export class WidgetConfigFormComponent implements OnInit {
  readonly colorsetStore = inject(ColorsetStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly layerStore = inject(DataLayerStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly typeStore = inject(DataLayerTypeStore);
  readonly widgetStore = inject(WidgetStore);
  readonly imageStore = inject(ImageStore);

  @Input() config!: WidgetConfig;
  @Input() type!: WidgetType;
  @Input() options!: ConfigFormOptions;
  @Output() configChange = new EventEmitter<WidgetConfig>();

  activeWidget: CustomGridStackWidget | null = null;
  configForm!: FormGroup;
  teaserImages: TeaserImage[] = [];
  radiusProperty: string | null = null;
  axisProperties: string[] = [];
  groupOptions: DropdownItem[] = [];
  availableConfig: string[] = [];
  selectedDataLayerType: DataLayerType | null = null;
  selectedDataLayer: DataLayer | null = null;
  isSingleAxisType = true;
  isPoiType = false;
  focusOptions: DropdownItem[] = [];
  numberOfScalesOptions: DropdownItem[] = [];

  /**
   * Initializes the focus options for the component with predefined configurations.
   * Sets the focus options array with label, alt text, help description, value, and an icon for each focus option.
   *
   * @return {void} This method does not return a value.
   */
  initFocusOptions(): void {
    this.focusOptions = [
      {
        label: 'Indikatoren und Teilräume',
        alt: 'Jahre',
        help: 'Das Diagramm stellt ein Jahr und mehrerer Indikatoren sowie Teilräume dar.',
        value: 'dataLayer',
        //icon: 'fas fa-calendar-days',
      },
      {
        label: 'Jahre und Teilräume',
        alt: 'Indikatoren',
        help: 'Das Diagramm stellt einen Indikator und mehrere Jahre sowie Teilräume dar.',
        value: 'indicator',
        //icon: 'fas fa-ruler-vertical',
      },
      {
        label: 'Indikatoren und Jahre',
        alt: this.isPoiType ? 'Standorte' : 'Teilräume',
        help: 'Das Diagramm stellt ein Teilraum und mehrere Jahre sowie Indikatoren dar.',
        value: 'feature',
        //icon: 'fas fa-map-location-dot',
      },
    ];
  }

  textAlignmentDropdownItems: DropdownItem[] = [
    { label: 'Oben', value: 'top', icon: 'fas fa-chevron-up' },
    { label: 'Rechts', value: 'right', icon: 'fas fa-chevron-right' },
    { label: 'Unten', value: 'bottom', icon: 'fas fa-chevron-down' },
    { label: 'Links', value: 'left', icon: 'fas fa-chevron-left' },
    { label: 'ohne', value: null, icon: 'fas fa-rectangle-xmark' },
  ];

  constructor(
    private fb: FormBuilder,
    private gridWidgetConfigService: GridWidgetConfigService,
    private widgetHelper: WidgetHelperService
  ) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      this.activeWidget = this.widgetStore.selected();
    });
  }

  /**
   * Initializes the component by setting up the required form, loading the active widget
   * and its associated data, and configuring widget-specific settings. Handles logic for
   * different widget types, including axis-based, single-axis, teaser, and point types,
   * and prepares the necessary options and configurations.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.initForm();
    this.activeWidget = this.widgetStore.selected();
    if (this.activeWidget) {
      this.selectedDataLayerType = this.typeStore.getById(
        this.activeWidget.widget.dataLayerType
      );

      this.selectedDataLayer = this.layerStore.getById(
        this.activeWidget.widget.dataLayer
      );

      if (this.widgetStore.isAxisBasedType()) {
        this.setFixedOptions();
      }

      this.availableConfig =
        this.gridWidgetConfigService.getDataLayerFieldsByType(
          this.activeWidget.type
        );

      if (this.type === 'teaser') {
        this.teaserImages = this.gridWidgetConfigService.getPoolImages();
      }

      this.isSingleAxisType = this.widgetHelper.isSingleAxisChart(this.type);
      this.isPoiType = !!this.typeStore.isPointType(
        this.activeWidget.widget.dataLayerType
      );
      this.getNumberOfScalesByColors();
      this.initFocusOptions();
    }
  }

  /**
   * Sets the indicator for the widget and updates the chart data.
   *
   * @param {Indicator} [indicator] - The indicator object to set. If not provided, the indicator will be cleared.
   * @return {void}
   */
  setIndicator(indicator?: Indicator): void {
    this.widgetStore.setWidgetProperty('indicator', indicator?._id || '');
    this.updateChartData();
  }

  /**
   * Updates the widget's data layer and corresponding chart data.
   *
   * @param {DataLayer} [dataLayer] - The data layer object containing properties such as ID and year. If not provided, default values will be used.
   * @return {void} This method does not return a value.
   */
  setDataLayer(dataLayer?: DataLayer): void {
    this.widgetStore.setWidgetProperties({
      dataLayer: dataLayer?._id ?? '',
      year: String(dataLayer?.year || ''),
    });
    this.updateChartData(dataLayer?._id);
  }

  /**
   * Sets the focus property of the widget and updates related properties.
   *
   * @param {DropdownChangeEvent} property - The dropdown change event containing the new focus property value.
   * @return {void} This method does not return any value.
   */
  setFocusProperty(property: DropdownChangeEvent): void {
    const nonFocusProperties = this.getNonFocusProperties().map(
      menuItem => menuItem.value
    );
    const updates: Partial<WidgetConfig> = {
      focusProperty: property.value,
      groupProperty: nonFocusProperties[0] as DataGroupProperty,
    };
    if (property.value !== 'dataLayer') {
      updates['sort'] = {
        enable: true,
        property: 'label',
        direction: 'asc',
      };
    }

    this.widgetStore.setWidgetProperties(updates, true);

    this.setFixedOptions();
    this.updateChartData();
  }

  /**
   * Updates the active widget with the provided data layer information and refreshes the associated chart data.
   *
   * @param {DataLayer} dataLayer - The data layer object containing the updated data and related information.
   * @return {void} Does not return a value.
   */
  onDataLayerUpdate(dataLayer: DataLayer): void {
    if (!this.activeWidget) return;
    this.activeWidget.widget.dataLayer = dataLayer._id;
    this.activeWidget.widget.year = dataLayer.year + '';
    this.widgetStore.update(this.activeWidget.id, this.activeWidget);
    this.updateChartData(dataLayer._id);
  }

  /**
   * Updates the list of disabled years for the active widget and refreshes the chart data.
   *
   * @param {number[]} years - An array of years to be marked as disabled.
   * @return {void} This method does not return a value.
   */
  onDisabledYearsUpdate(years: number[]): void {
    if (this.activeWidget) {
      this.widgetStore.setWidgetProperty('disabledYears', years);
      this.updateChartData();
    }
  }

  /**
   * Updates the disabled features for the currently active widget and selected data layer.
   *
   * @param {string[]} keys - Array of keys representing the disabled features.
   * @return {void} Does not return a value.
   */
  onDisabledFeaturesUpdate(keys: string[]): void {
    if (this.activeWidget && this.selectedDataLayer) {
      this.widgetStore.setWidgetProperty('disabledFeatures', keys);
      this.updateChartData(this.selectedDataLayer._id);
    }
  }

  /**
   * Updates the map charts configuration to disable specified charts
   * and refreshes the chart data for the selected data layer.
   *
   * @param {string[]} keys - An array of chart keys to be disabled in the map.
   * @return {void} This method does not return a value.
   */
  onDisabledMapChartsUpdate(keys: string[]): void {
    if (this.activeWidget) {
      this.widgetStore.setWidgetProperty('disabledCharts', keys);
      this.updateChartData();
    }
  }

  /**
   * Sets the group property for the widget and updates the chart data.
   *
   * @param {DataGroupProperty} property - The group property to be set for the widget.
   * @return {void} Does not return any value.
   */
  setGroupProperty(property: DataGroupProperty): void {
    this.widgetStore.setWidgetProperty('groupProperty', property);
    this.updateChartData();
  }

  /**
   * Retrieves the properties from the focusOptions array that do not match the current active widget's focus property.
   *
   * @return {Array} An array of properties not matching the focus property of the active widget.
   */
  getNonFocusProperties(): DropdownItem[] {
    return this.focusOptions.filter(
      item => item.value !== this.activeWidget?.widget.focusProperty
    );
  }

  /**
   * Configures and sets fixed options for axis properties and group options
   * based on the configuration of the active widget and its data set properties.
   *
   * The method evaluates the type of widget and its associated data layers,
   * indicators, or features to build `groupOptions`. It also initializes
   * `axisProperties` based on these `groupOptions`. Additionally, for widgets of
   * type 'bubble', it considers the third group's value for setting the radius
   * property before initializing axis properties.
   *
   * @return {void} This method does not return a*/
  setFixedOptions(): void {
    this.axisProperties = [];
    this.groupOptions = [];
    const check = this.widgetStore.isAxisBasedType();
    const conf = this.activeWidget?.widget;

    if (!check || !conf) {
      this.setAxisProperties();
      this.updateChartData();
      return;
    }

    const group = this.widgetStore.dataSetProperty(conf);
    switch (group) {
      case 'dataLayer':
        this.groupOptions = conf.dataLayers.map(layerId => {
          const dataLayer = this.layerStore.getById(layerId);
          return {
            label: dataLayer.year + '',
            value: dataLayer._id,
          };
        });
        break;
      case 'indicator':
        conf.indicators;
        this.groupOptions = conf.indicators.map(indicatorId => {
          const indicator = this.indicatorStore.getById(indicatorId);
          return {
            label: indicator.name,
            value: indicator._id,
          };
        });
        break;
      case 'feature':
        if (!conf.features) break;
        for (const key in conf.features) {
          this.groupOptions.push({
            label: conf.features[key].name,
            value: key,
          });
        }
        break;
    }

    const t = this.groupOptions?.slice(0, 2)?.map(opt => opt.value || '') || [];
    this.axisProperties = [...t] as string[];
    if (
      this.activeWidget?.type === 'bubble' &&
      this.groupOptions[2] &&
      this.groupOptions[2].value
    ) {
      this.radiusProperty = this.groupOptions[2].value as string;
      this.setAxisProperties();
    } else {
      this.setAxisProperties();
    }
  }

  /**
   * Configures and sets the axis properties for a chart widget.
   * Updates the widget properties in the store and refreshes the chart data.
   *
   * @return {void} Does not return a value.
   */
  setAxisProperties(): void {
    this.widgetStore.setWidgetProperties({
      axisProperties: this.axisProperties || [],
      radiusProperty: this.radiusProperty || '',
    });
    this.updateChartData();
  }

  /**
   * Swaps the current axis properties of the widget and updates the widget properties and chart data accordingly.
   *
   * @return {void} Does not return a value.
   */
  swapAxisProperties(): void {
    this.axisProperties = [...swap(this.axisProperties)];
    this.widgetStore.setWidgetProperties({
      axisProperties: this.axisProperties,
    });
    this.updateChartData();
  }

  /**
   * Sets the selected feature in the widget store and updates the chart data accordingly.
   *
   * @param {string} feature - The feature to be selected and set in the widget store.
   * @return {void}
   */
  selectFeature(feature: string): void {
    this.widgetStore.setWidgetProperty('feature', feature);
    this.updateChartData();
  }

  /**
   * Sets the radius property for the widget and updates the chart data accordingly.
   *
   * @return {void} No return value.
   */
  setRadius(): void {
    this.widgetStore.setWidgetProperty(
      'radiusProperty',
      this.radiusProperty || ''
    );
    this.updateChartData();
  }

  toggleCharts(): void {
    if (this.activeWidget?.widget.map?.captions) {
      this.activeWidget.widget.map.captions.chart = true;

      this.configForm.patchValue({ map: { captions: { chart: true } } });
      this.widgetStore.setWidgetProperties(this.activeWidget?.widget, true);
      this.updateChartData();
    }
  }

  /**
   * update districts and data
   */
  /**
   * Updates the chart data based on the provided data layer ID.
   * Handles different logic depending on the context of the active widget.
   *
   * @param {string} [dataLayerId] - An optional ID for the data layer to update districts for the active widget.
   * @return {void} Does not return a value.
   */
  updateChartData(dataLayerId?: string): void {
    if (this.activeWidget && this.activeWidget.widget.context === 'external') {
      //this.widgetStore.rerenderWidget(this.activeWidget._id);
    } else {
      if (dataLayerId && this.activeWidget) {
        this.widgetStore.updateDistricts(this.activeWidget._id, dataLayerId);
      }
    }
    setTimeout(() => {
      this.widgetStore.updateChartData();
    }, 10);
  }

  /**
   * Initializes the form configuration for the grid widget.
   * Retrieves the form fields based on the type and current configuration,
   * enhances the form group with additional properties, and sets up a subscription
   * to handle form value changes.
   *
   * @return {void} No return value.
   */
  private initForm(): void {
    this.configForm = this.gridWidgetConfigService.getFormFieldsByType(
      this.type,
      this.config
    );

    this.configFormGroupTypeEnhancer();

    // Handle changes from the form
    this.configForm.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      const confPartial = { ...value };
      this.configChange.emit(confPartial);
    });
  }

  /**
   * Enhances the form group configuration based on the current type of the component.
   * Calls specific methods to modify or set up the form array if the type matches certain cases.
   *
   * @return {void} Does not return a value.
   */
  configFormGroupTypeEnhancer(): void {
    switch (this.type) {
      case 'teaser':
        this.setTeaserFormArray();
        break;
      case 'map':
        this.setPoiFormArray();
        break;
      case 'bubble':
      case 'scatter':
      case 'bar':
      case 'line':
      case 'polarArea':
      case 'pie':
      case 'table':
      case 'text':
      case 'info':
      default:
        break;
    }
  }

  /**
   * Populates the teaser form array with items from the configured teaser items.
   * If no items are present in the configuration, it initializes the array with a default teaser.
   *
   * @return {void} This method does not return any value.
   */
  setTeaserFormArray(): void {
    const items = this.config.teaser?.items;
    if (items) {
      for (const item of items) {
        this.addTeaser(item);
      }
    } else {
      this.addTeaser();
    }
  }

  /**
   * Populates the teaser form array with items from the configured teaser items.
   * If no items are present in the configuration, it initializes the array with a default teaser.
   *
   * @return {void} This method does not return any value.
   */
  setPoiFormArray(): void {
    const poi = this.config.map?.poi;
    if (poi) {
      for (const item of poi) {
        this.addPoiGroup(item);
      }
    }
  }

  /**
   * Adds a teaser object to the teaser form array. The teaser object includes various fields such as topmark, header, content,
   * background color, background source, bookmark, hyperlink, and link caption. Default values are applied for missing fields in the provided teaser item.
   *
   * @param {TeaserItem} [teaserItem] - The teaser item to be added to the form, containing values for fields such as topmark, header, content, background color (validated as a valid hex color), background source, bookmark, hyperlink (validated as a valid URL), and link caption. Defaults to empty strings or predefined values if not provided.
   * @return {void} This method does not return anything.
   */
  addTeaser(teaserItem?: TeaserItem): void {
    const group = this.fb.group({
      topmark: [teaserItem?.topmark || ''],
      header: [teaserItem?.header || ''],
      content: [teaserItem?.content || ''],
      backgroundColor: [
        teaserItem?.backgroundColor || '#FFFFFF',
        [Validators.pattern(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)], // Hex color validation],
      ],
      backgroundId: [teaserItem?.backgroundId || null],
      backgroundSrc: [teaserItem?.backgroundSrc || ''],
      backgroundCopyright: [teaserItem?.copyright || ''],
      bookmark: [teaserItem?.bookmark || ''],
      topic: [teaserItem?.topic || ''],
      story: [teaserItem?.story || ''],
      href: [teaserItem?.href || '', hrefValidator()],
      linkCaption: [teaserItem?.linkCaption || ''],
    });
    this.teaserFormArray.push(group);
  }

  /**
   *
   */
  addPoiGroup(poi: PoiConf): void {
    if (!this.poiFormArray) return;
    const isDisabled = !poi.indicators || poi.indicators.length > 1;
    const group = this.fb.group({
      type: [poi.type || ''],
      indicators: [poi.indicators || ''],
      legend: this.fb.group({
        colorset: [poi.legend?.colorset || ''],
        invert: [poi.legend?.invert || false],
        legendScope: [
          {
            value:
              poi.legend?.legendScope ||
              this.activeWidget?.widget?.map?.legend?.legendScope ||
              'all',
            disabled: isDisabled,
          },
        ],
        scaleMode: [
          {
            value:
              poi.legend?.scaleMode ||
              this.activeWidget?.widget?.map?.legend?.scaleMode ||
              'width',
            disabled: isDisabled,
          }, // <-- add default
        ],
        numberOfScales: [
          {
            value:
              poi.legend?.numberOfScales ||
              this.activeWidget?.widget?.map?.legend?.numberOfScales ||
              '',
            disabled: isDisabled,
          },
        ],
      }),
    });
    this.poiFormArray.push(group);
  }

  /**
   * Retrieves the FormArray associated with the 'teaser.items' path from the configuration form.
   *
   * @return {FormArray} The FormArray object representing the 'teaser.items' path in the form.
   */
  public get teaserFormArray(): FormArray {
    return this.configForm.get('teaser')?.get('items') as FormArray;
  }

  /**
   * Retrieves the form array for points of interest (POI) from the configuration form.
   *
   * @return {FormArray} The FormArray instance representing the POI data within the map configuration form.
   */
  public get poiFormArray(): FormArray {
    return this.configForm.get('map')?.get('poi') as FormArray;
  }

  /**
   * Handles the change event for a teaser image and updates the provided control's value accordingly.
   *
   * @param {string} teaserImageId - The teaser image object containing information such as `_id` or `src`.
   * @param {any} control - The form control to be updated with the teaser image properties.
   * @return {void} This method does not return a value.
   */
  onTeaserImageChange(teaserImageId: string, control: any): void {
    const image = this.imageStore.getById(teaserImageId);
    if (image) {
      control.patchValue({
        backgroundId: teaserImageId,
        backgroundSrc: teaserImageId,
      });
    } else {
      control.patchValue({
        backgroundId: undefined,
        backgroundSrc: teaserImageId,
      });
    }
  }

  hasFixedColorset(): false | Colorset {
    const indicator: Indicator = this.indicatorStore.getById(
      this.config.indicator
    );
    if (!indicator) return false;
    let data: IndicatorData | undefined = undefined;
    let colorset: Colorset | undefined = undefined;

    if (this.config.indicator && this.config.dataLayer) {
      data = this.dataStore.getByDataLayerAndIndicator(
        this.config.dataLayer,
        this.config.indicator
      );
    }
    if (!data) return false;
    if (data.colorSet) {
      colorset = this.colorsetStore.getById(data.colorSet);
    }
    if (colorset) return colorset;
    if (indicator.defaultColorSet) {
      colorset = this.colorsetStore.getById(indicator.defaultColorSet);
    }
    if (colorset) return colorset;

    return false;
  }

  removeImageFromSlide(control: any) {
    control.patchValue({
      backgroundId: undefined,
      backgroundSrc: undefined,
    });
  }

  /**
   * Handles the change event when the color value is updated.
   *
   * @param {any} event - The event containing the updated color value.
   * @param {any} control - The control object used to update the background color value.
   * @return {void} Does not return a value.
   */
  onColorChange(event: any, control: any): void {
    if (event.value.length === 7) {
      control.get('backgroundColor')?.setValue(event.value);
    }
  }

  /**
   * Handles the text color change event, and updates the background color value in the specified control
   * if the input value's length equals 7.
   *
   * @param {any} event - The event object triggered by the text color input change.
   * @param {any} control - The form control whose background color value needs to be updated.
   * @return {void} This method does not return any value.
   */
  onTextColorChange(event: any, control: any): void {
    if (event.target.value.length === 7) {
      control.get('backgroundColor')?.setValue(event.target.value);
    }
  }

  /**
   * Handles the submission of the configuration form.
   * If the form is valid, emits the form's value through the configChange event emitter.
   *
   * @return {void} Does not return a value.
   */
  onSubmit() {
    if (this.configForm.valid) {
      this.configChange.emit(this.configForm.value);
    }
  }

  /**
   * Generates and updates the number of scale options based on the provided color set.
   *
   * @param {Colorset} [colorset] - Optional argument specifying the set of colors
   *                                to be used for calculating the number of scales.
   *                                If not provided, fallback to the configured colorset
   *                                or a default set of colors.
   * @return {void} This method does not return a value. It updates the number of scale options.
   */
  getNumberOfScalesByColors(colorset?: Colorset): void {
    const colorsetId = this.config.colorset;
    const colors =
      colorset?.colors ||
      this.colorsetStore.getById(colorsetId)?.colors ||
      fallbackColors;
    const size = colors.length;
    const options: DropdownItem[] = [];

    for (let i = 1; i <= size; i++) {
      options.push({
        label: String(i),
        value: i,
      });
    }

    if (
      this.configForm.value.map?.legend &&
      Number(this.configForm.value.map.legend.numberOfScales) > options.length
    ) {
      setTimeout(() => {
        this.configForm.patchValue({
          map: {
            legend: {
              numberOfScales: options.length,
            },
          },
        });
      });
    }

    this.numberOfScalesOptions = options;
  }

  /**
   * Checks if the form contains any of the specified fields.
   *
   * @param {string[]} fields - An array of field names to check for in the form.
   * @return {boolean} Returns true if the form contains at least one of the specified fields, otherwise false.
   */
  formContains(fields: string[]): boolean {
    return fields.some(field => this.configForm.contains(field));
  }

  protected readonly mapLegendOptions = mapLegendOptions;
  protected readonly chartLegendOptions = chartLegendOptions;

  /**
   * Updates the configuration form with the provided colorset ID.
   *
   * @param {Colorset | null} $event - The colorset object containing the ID to patch, or null if no colorset is provided.
   * @return {void} This method does not return a value.
   */
  patchColorset($event: Colorset | null): void {
    this.configForm.patchValue({ colorset: $event?._id });
    this.getNumberOfScalesByColors($event || undefined);
  }

  protected readonly leafletTileLayerOptions = leafletTileLayerOptions;

  /**
   * Retrieves the 'href' control from a specified index within the 'teaser.items' FormArray.
   *
   * @param {number} index - The index of the item within the 'teaser.items' FormArray.
   * @return {AbstractControl | null} The 'href' control at the specified index, or null if not found.
   */
  getHrefControl(index: number): AbstractControl | null {
    const items = this.configForm.get('teaser.items') as FormArray;
    return items?.at(index)?.get('href') || null;
  }

  protected readonly mapLegendPositionOptions = mapLegendPositionOptions;
  protected readonly mapTooltipStyleDropdownItems =
    mapTooltipStyleDropdownItems;

  /**
   * Applies a preset configuration to the widget
   *
   * @param {ChartPreset} preset - The preset configuration to apply
   * @return {void}
   */
  applyPreset(preset: ChartPreset): void {
    const updates: Partial<WidgetConfig> = {
      focusProperty: preset.focusProperty,
      groupProperty: preset.groupProperty,
    };

    if (preset.focusProperty !== 'dataLayer') {
      updates['sort'] = {
        enable: true,
        property: 'label',
        direction: 'asc',
      };
    }

    this.widgetStore.setWidgetProperties(updates, true);
    this.setFixedOptions();
    this.updateChartData();
  }

  /**
   * Gets the visual configuration data for the chart diagram
   *
   * @return {object} The configuration data for visual display
   */
  getVisualConfigData() {
    if (!this.activeWidget) return null;
    return {
      focusProperty: this.activeWidget.widget.focusProperty,
      groupProperty: this.activeWidget.widget.groupProperty,
      isSingleAxis: this.isSingleAxisType,
      isPoiType: this.isPoiType,
    };
  }

  /**
   * Retrieves a Point of Interest (POI) object from the configuration based on the provided index.
   *
   * @param {string} index - The index used to locate the POI in the configuration.
   * @return {DataLayerType|null} The POI object if found, or null if the index is invalid or the POI is not found.
   */
  getPoiFromConfig(index: number): DataLayerType | null {
    const poiConf = this.getPoiConf(index);
    if (!poiConf) return null;
    const poiId = poiConf.type;
    if (!poiId) return null;
    const poi = this.typeStore.getById(poiId);
    return poi || null;
  }

  getPoiConf(index: number): PoiConf | null {
    const poiConf = this.activeWidget?.widget?.map?.poi;
    if (!poiConf) return null;
    return poiConf[index] || null;
  }
}
